/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, Inject, OnInit } from '@angular/core';
import { ITextButton } from 'libs/sysque/shared/ui/src/lib/components/sysque-text-button/sysque-text-button-types';
import {
  IProjectDataFacade,
  PROJECT_DATA_FACADE_TOKEN,
} from '../../../../../data-access/src/lib/domain/facades-models/project-data-facade-interface';
import { GlobalConstants } from '../../constants/global-constants';
import { IFamilyInformationItem, Param, ParamSize } from '../add-family-information/add-family-information-types';
import { AssignProjectDataReceiveController } from '../../receive-controllers/assign-project-data-receive-controller';
import { ReceiveMessageHandler } from '@mep/web-browser-bridge';
import { LocalStorageService } from '../../../../../utils/src/lib/local-storage/local-storage.service';
import { AlertStatus, IAlert } from '../../../../../../shared/ui/src/lib/components/alert/alert-types';

@Component({
  selector: 'sysque-assign-project-parameter-values',
  templateUrl: './assign-project-parameter-values.component.html',
  styleUrls: ['./assign-project-parameter-values.component.scss'],
  standalone: false
})
export class AssignProjectParameterValuesComponent implements OnInit {
  public componentTitle = '';
  public alertStatus = AlertStatus.Warning;
  public warningMessage = '';
  public paramSizes: ParamSize[];
  public paramName = '';
  public paramIndex = -1;
  public item: IFamilyInformationItem;
  public localParams: Param[];
  public isContainSizes = true;
  public get alert(): IAlert {
    return {
      message: this.warningMessage,
      type: this.alertStatus,
      isDismissable: true,
    };
  }

  constructor(
    @Inject(PROJECT_DATA_FACADE_TOKEN) private facade: IProjectDataFacade,
    private readonly receiveMessageHandler: ReceiveMessageHandler,
    public localStorageService: LocalStorageService
  ) {
    this.receiveMessageHandler.registerController(new AssignProjectDataReceiveController(this.localStorageService));
  }

  /**
   * Initializes the component after Angular has initialized all data-bound properties.
   * It sets up a storage event listener, retrieves project parameters from storage,
   * and initializes the component's properties.
   */
  public ngOnInit(): void {
    this.componentTitle = 'Assign Project Parameter Values';
    window.addEventListener('storage', this.storageEventListener.bind(this));
    const projectParam = this.facade.getProjectParametersNeedToModify(true) as Param;
    this.paramName = projectParam.name;
    this.paramIndex = projectParam.index;
    this.item = this.facade.getEditFamilyItem();
    this.initializeProjectParameters(projectParam);
  }

  /**
   * Lifecycle hook that is called when the component is destroyed.
   * It removes the event listener for the 'storage' event.
   */
  public ngOnDestroy(): void {
    window.removeEventListener('storage', this.storageEventListener.bind(this));
  }

  /**
   * Handles the storage event and updates the project parameters based on the event data.
   * @param event - The storage event object.
   */
  public storageEventListener(event: StorageEvent): void {
    if (
      event &&
      event.key === GlobalConstants.IMPORT_EXPORT_EXCEL_RESPONSE &&
      event.newValue !== event.oldValue &&
      event.newValue !== null
    ) {
      const data = JSON.parse(event.newValue);
      switch (data.response) {
        case 1:
          this.warningMessage = GlobalConstants.FAILED_TO_IMPORT;
          this.getWarningInfo(AlertStatus.Warning);
          break;
        case 2:
          this.getWarningInfo(AlertStatus.Success);
          this.warningMessage = GlobalConstants.EXPORTED_SUCCESSFULLY + this.item.name + '.xls.';
          break;
        case 3:
          this.getWarningInfo(AlertStatus.Warning);
          this.warningMessage = GlobalConstants.ASSIGN_PROJECT_PARAMETER_ERROR;
          break;
        case 4:
          this.paramSizes = [];
          data.paramSizes.forEach((element) => {
            this.paramSizes.push({ id: element.Id, value: element.Value });
          });

          this.warningMessage = GlobalConstants.IMPORTED_SUCCESSFULLY;
          this.getWarningInfo(AlertStatus.Success);
          break;
      }
      this.facade.updateLocalStorage(GlobalConstants.IMPORT_EXPORT_EXCEL_RESPONSE, null);
    }
  }

  /**
   * Returns the CSS class name for a given row based on the index and project parameter.
   * @param index - The index of the row.
   * @param projectParameter - The project parameter.
   * @returns The CSS class name for the row.
   */
  public getClassForRow(index: number): string {
    if (index % 2 === 0) {
      return GlobalConstants.ODD_BACKGROUND_UNSELECTED;
    } else if (index % 2 !== 0) {
      return GlobalConstants.EVEN_BACKGROUND_UNSELECTED;
    }
  }

  /**
   * Handles the click event when the dismiss button is clicked.
   */
  public onDismissClick(): void {
    this.facade.changeDialogDimensions(615);
    this.facade.updateLocalStorage(GlobalConstants.IMPORT_EXPORT_EXCEL_RESPONSE, null);
    this.warningMessage = '';
  }

  /**
   * Handles the change in parameter name value.
   *
   * This method updates the value of a parameter size in the `paramSizes` array.
   * If the parameter size is the first element in the array, it updates the value
   * for all elements in the array. Otherwise, it updates the value for the specific
   * parameter size.
   *
   * @param newSizeValue - The new value to be assigned to the parameter size.
   * @param parameterSize - The parameter size object that needs to be updated.
   */
  public onParameterNameValueChange(newSizeValue: string, parameterSize: ParamSize): void {
    const index = this.paramSizes.findIndex((x) => x.id === parameterSize.id);
    if (index === 0) {
      this.paramSizes.forEach((x) => (x.value = newSizeValue));
    } else {
      this.paramSizes[index].value = newSizeValue;
    }
  }

  /**
   * Gets the import button values.
   * @returns The import button values.
   */
  public get importButtonValues(): ITextButton<void> {
    return {
      name: 'Import from Excel',
      width: '100%',
      isBordered: false,
      imageSrc: '',
      click: () => {
        this.facade.importExcelClick(this.paramSizes);
      },
    };
  }

  /**
   * Gets the configuration for the export button.
   * @returns The configuration object for the export button.
   */
  public get exportButtonValues(): ITextButton<void> {
    return {
      name: 'Export',
      width: '100%',
      isBordered: false,
      imageSrc: '',
      click: () => {
        this.facade.exportExcelClick(this.paramSizes, this.item.name);
      },
    };
  }

  /**
   * Saves the data by updating the project parameter values and closing the window.
   */
  public saveData(): void {
    this.facade.updateLocalStorage(GlobalConstants.UPDATE_PROJECT_PARAMS_SIZES, {
      isSaved: 'true',
      paramSizes: this.paramSizes,
    });
    this.facade.closeWindow();
  }

  /**
   * Gets the warning information.
   * Sets the alert type to Warning and changes the dialog dimensions.
   */
  private getWarningInfo(alertStatus: AlertStatus): boolean {
    this.alertStatus = alertStatus;
    this.facade.changeDialogDimensions(680);
    return false;
  }

  /**
   * Initializes the project parameters based on the provided `projectParam`.
   * If `projectParam.lstSizes` is not empty and `projectParam.name` is not empty,
   * the `projectParameters` will be set to `projectParam.lstSizes`.
   * Otherwise, the `projectParameters` will be populated with `ParamSize` objects
   * created from the `item.sizeRange` property.
   * @param projectParameter The project parameter to initialize.
   */
  private initializeProjectParameters(projectParameter: Param): void {
    const sizeRange = this.item.sizeRange.split(',').filter((x) => !x.includes('*'));
    const validParamSizes = projectParameter.lstSizes.filter((x) => sizeRange.includes(x.id));
    if (validParamSizes?.length > 0 && projectParameter.name !== '') {
      this.paramSizes = validParamSizes;
    } else {
      this.paramSizes = [];
      this.item.sizeRange
        .split(',')
        .filter((x) => !x.includes('*'))
        .forEach((element) => {
          const paramSize: ParamSize = {
            id: element,
            value: '',
          };
          this.paramSizes.push(paramSize);
        });
    }
    if (this.item.sizeRange === '') {
      this.isContainSizes = false;
      return;
    }
  }
}
